/**
 * API methods for curated books (精选书库).
 * Mixin pattern — these functions use `this` and are assigned to ApiClient.prototype.
 */

import { API_BASE } from './helpers';
import * as localDB from '../localDB';

export async function getCuratedBooks() {
  const resp = await fetch(`${API_BASE}/reader/curated-books`, {
    headers: { 'Authorization': `Bearer ${this.token}` },
  });
  if (!resp.ok) throw new Error(`getCuratedBooks failed: ${resp.status}`);
  return resp.json();
}

export async function addCuratedBookToLibrary(curatedBookId) {
  const resp = await fetch(`${API_BASE}/reader/curated-books/${curatedBookId}/add-to-library`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const err = new Error(body.error || `addCuratedBook failed: ${resp.status}`);
    err.code = body.code;
    throw err;
  }
  return resp.json();
}

/**
 * Full flow: create backend record → download EPUB → save locally → write IndexedDB.
 * This order avoids "clickable but unreadable" half-success records when download fails.
 * Returns { book_id } on success so the caller can navigate.
 */
export async function addCuratedBookFull(curatedBook, onProgress) {
  const result = await this.addCuratedBookToLibrary(curatedBook.id);

  const fileResp = await fetch(`${API_BASE}/reader/books/${result.book_id}/file`, {
    headers: { 'Authorization': `Bearer ${this.token}` },
  });
  if (!fileResp.ok) {
    const downloadErr = new Error('Failed to download curated book file');
    downloadErr.code = 'CURATED_DOWNLOAD_FAILED';
    throw downloadErr;
  }

  const contentLength = parseInt(fileResp.headers.get('content-length') || '0', 10);
  if (onProgress && contentLength > 0 && fileResp.body) {
    const reader = fileResp.body.getReader();
    const chunks = [];
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      onProgress(Math.round((received / contentLength) * 100));
    }
    const merged = new Uint8Array(received);
    let offset = 0;
    for (const c of chunks) { merged.set(c, offset); offset += c.length; }
    await localDB.saveBookFile(result.book_id, merged.buffer);
  } else {
    const buf = await fileResp.arrayBuffer();
    await localDB.saveBookFile(result.book_id, buf);
    if (onProgress) onProgress(100);
  }

  const now = new Date().toISOString();
  await localDB.bulkPut('books', [{
    id: result.book_id,
    user_id: this.userId,
    title: curatedBook.title,
    author: curatedBook.author || null,
    file_name: `${curatedBook.title}.epub`,
    file_type: 'epub',
    // Real R2 path (e.g. "curated-books/xxx.epub") is stored in D1 by the
    // addCuratedBookToLibrary backend handler. The next sync will pull it
    // into the local IDB record. Writing `null` here instead of the old
    // `result.download_url || true` avoids:
    //   1. Storing a boolean `true` when download_url is missing (wrong type)
    //   2. Storing an API endpoint path instead of an R2 key (wrong semantics)
    //   3. clearCurrentUserData misidentifying this book as cloud-backed and
    //      deleting its local blob prematurely
    file_url: null,
    cover_url: null,
    total_word_count: curatedBook.total_words || null,
    language: curatedBook.language || null,
    language_confirmed_value: curatedBook.language || null,
    canonical_book_id: result.canonical_book_id,
    last_read_at: null,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  }]);

  return result;
}
